import { useState } from "react";
import { api, ApiError } from "../api";
import type { Row } from "../types";
import { IconClose, IconCheck, IconFlask } from "../icons";
import { ROTULO_SECAO } from "../config";

/**
 * TRAZER A NORMA PARA O CADASTRO (com IA).
 *
 * A pessoa cola o trecho da norma — ou o procedimento interno do laboratório,
 * ou a tabela do fabricante — e a IA transcreve aquilo em faixas.
 *
 * O que esta tela deixa explícito, e não pode ser suavizado: o que a IA traz
 * NÃO vale no diagnóstico. Fica guardado como pendente até alguém do
 * laboratório abrir a norma ao lado, conferir número por número e ativar. É
 * essa conferência que sustenta o laudo depois — a IA aqui é digitadora, não
 * perita, e um número errado que passasse batido sairia em documento assinado.
 */
interface Props {
  onCancel: () => void;
  onSaved: () => void;
}

export default function LimitesIA({ onCancel, onSaved }: Props) {
  const [texto, setTexto] = useState("");
  const [norma, setNorma] = useState("");
  const [contexto, setContexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<{
    ok: boolean;
    criadas: number;
    descartadas: number;
    mensagem: string;
    linhas?: Row[];
    detalhes?: { motivo: string; linha: Row }[];
  } | null>(null);
  /** Faixas já conferidas nesta sessão (some o botão e vira "conferida"). */
  const [conferidas, setConferidas] = useState<Set<number>>(new Set());

  async function enviar() {
    setErro(null);
    setEnviando(true);
    try {
      const r = await api.limites.sugerir({ texto, norma, contexto: contexto || undefined });
      setResultado(r);
      if (r.criadas > 0) onSaved();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível falar com a IA.");
    } finally {
      setEnviando(false);
    }
  }

  async function conferir(id: number) {
    try {
      await api.limites.conferir(id);
      setConferidas((s) => new Set(s).add(id));
      onSaved();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível ativar a faixa.");
    }
  }

  /** "≥ 30 kV", "até 20 mg/kg", "30 a 40 kV" — do jeito que se lê na norma. */
  function faixaEmPalavras(l: Row): string {
    const min = l.valor_min ?? null;
    const max = l.valor_max ?? null;
    const un = l.unidade ? ` ${l.unidade}` : "";
    if (min !== null && max !== null) return `${min} a ${max}${un}`;
    if (min !== null) return `≥ ${min}${un}`;
    return `≤ ${max}${un}`;
  }

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-card modal-wide" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head rf-head">
          <div className="rf-head-txt">
            <span className="rf-head-icon">
              <IconFlask size={20} />
            </span>
            <div>
              <h3>Trazer a norma para o cadastro</h3>
              <p className="rf-head-sub">
                Cole abaixo o trecho da norma (ou o procedimento de vocês) com as faixas. A IA
                transcreve para o cadastro — <strong>ela não decide nada</strong> e nenhuma faixa
                entra no diagnóstico antes de você conferir.
              </p>
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onCancel} title="Fechar (Esc)">
            <IconClose size={18} />
          </button>
        </div>

        <div className="conf-corpo">
          {!resultado && (
            <>
              <div className="ia-campos">
                <label className="field">
                  <span>De qual norma / documento é este texto?</span>
                  <input
                    value={norma}
                    onChange={(e) => setNorma(e.target.value)}
                    placeholder="ex.: ABNT NBR 10576:2017 — Tabela 3"
                  />
                </label>
                <label className="field">
                  <span>Vale só para algum caso? (opcional)</span>
                  <input
                    value={contexto}
                    onChange={(e) => setContexto(e.target.value)}
                    placeholder="ex.: classe 69 kV, óleo mineral"
                  />
                  {/*
                    Aviso que evita a dúvida clássica "cadastrei e o sistema não
                    usa": faixa restrita a uma classe/óleo só entra no
                    diagnóstico de análise que tenha o equipamento vinculado.
                  */}
                  <small className="muted">
                    Preencher aqui RESTRINGE: a faixa passa a valer só para análises com o
                    equipamento vinculado a essa classe/óleo. Em dúvida, deixe vazio — assim
                    ela vale para todas.
                  </small>
                </label>
              </div>

              <label className="field">
                <span>Texto da norma com as faixas</span>
                <textarea
                  className="ia-texto"
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  rows={12}
                  placeholder={
                    "Cole aqui a tabela ou o texto. Exemplo do que funciona bem:\n\n" +
                    "Rigidez dielétrica (disco): mínimo 30 kV — satisfatório; de 25 a 30 kV — " +
                    "acompanhar; abaixo de 25 kV — insatisfatório.\n" +
                    "Teor de água: até 20 mg/kg satisfatório; de 20 a 35 mg/kg acompanhar..."
                  }
                />
              </label>

              <div className="conf-aviso atencao">
                A IA copia <strong>só o que estiver no texto colado</strong>. Ela não conhece a
                tabela da ABNT de cabeça — se você não colar os valores, ela devolve vazio (e é
                assim que tem de ser: número inventado aqui vira laudo errado lá na frente).
              </div>

              {erro && <div className="conf-aviso erro">{erro}</div>}
            </>
          )}

          {resultado && (
            <>
              <div className={`conf-aviso ${resultado.criadas > 0 ? "ok" : "atencao"}`}>
                {resultado.mensagem}
              </div>

              {resultado.linhas && resultado.linhas.length > 0 && (
                <div className="conf-lista">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Seção</th>
                        <th>Parâmetro</th>
                        <th>Faixa</th>
                        <th>Veredito</th>
                        <th>De onde a IA tirou</th>
                        <th className="col-actions"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultado.linhas.map((l) => (
                        <tr key={l.id}>
                          <td>{ROTULO_SECAO[l.secao] ?? l.secao}</td>
                          <td>
                            <strong>{l.rotulo ?? l.parametro}</strong>
                          </td>
                          <td>{faixaEmPalavras(l)}</td>
                          <td>{l.veredito}</td>
                          <td className="ia-trecho">{l.observacao ?? "—"}</td>
                          <td className="col-actions">
                            {conferidas.has(l.id) ? (
                              <span className="pill pill-ok">conferida</span>
                            ) : (
                              <button
                                type="button"
                                className="btn-secondary btn-sm"
                                title="Conferi contra a norma — pode valer no diagnóstico"
                                onClick={() => void conferir(l.id)}
                              >
                                <IconCheck size={14} /> Conferi
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {resultado.descartadas > 0 && (
                <div className="conf-aviso atencao">
                  {resultado.descartadas} linha(s) foram descartadas por não encaixarem no cadastro
                  {resultado.detalhes?.length ? (
                    <ul className="ia-descartes">
                      {resultado.detalhes.map((d, i) => (
                        <li key={i}>
                          {d.motivo} — “{String(d.linha?.rotulo ?? d.linha?.parametro ?? "?")}”
                        </li>
                      ))}
                    </ul>
                  ) : (
                    "."
                  )}
                </div>
              )}

              {erro && <div className="conf-aviso erro">{erro}</div>}
            </>
          )}
        </div>

        <div className="modal-foot">
          {!resultado ? (
            <>
              <button className="btn-secondary" onClick={onCancel} disabled={enviando}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={() => void enviar()}
                disabled={enviando || texto.trim().length < 40 || !norma.trim()}
              >
                <IconFlask size={16} /> {enviando ? "Lendo a norma…" : "Trazer as faixas"}
              </button>
            </>
          ) : (
            <>
              <button
                className="btn-secondary"
                onClick={() => {
                  setResultado(null);
                  setTexto("");
                }}
              >
                Colar outro trecho
              </button>
              <button className="btn-primary" onClick={onCancel}>
                Fechar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
